/**
 * Singleton de WebSocket fora do React.
 *
 * Mantém UMA conexão por aba durante toda a sessão. O hook useWebSocket apenas
 * controla connect/disconnect (via isAuthenticated) e registra observadores —
 * sem useEffect com deps que possam re-disparar e abrir conexões duplicadas.
 *
 * Reconnect com backoff exponencial, ping/pong para evitar idle close do API
 * Gateway (10 min) e polling REST como fallback enquanto reconecta.
 */

const RECONNECT_DELAY_BASE = 1000;
const RECONNECT_DELAY_MAX = 30000;
const PING_INTERVAL_MS = 4 * 60 * 1000;
const PONG_TIMEOUT_MS = 9 * 60 * 1000;

export type WsStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface WsEvent {
  type: string;
  data?: unknown;
  timestamp?: string;
}

type EventHandler = (event: WsEvent) => void;
type StatusHandler = (status: WsStatus) => void;
type TokenProvider = () => Promise<string | null>;

class WsClient {
  private url: string | null = null;
  private getToken: TokenProvider | null = null;
  private ws: WebSocket | null = null;
  private status: WsStatus = 'idle';
  private isConnecting = false;
  private wantConnected = false;
  private reconnectAttempt = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private lastPongAt = Date.now();
  private eventHandlers = new Set<EventHandler>();
  private statusHandlers = new Set<StatusHandler>();

  configure(url: string, getToken: TokenProvider) {
    this.url = url;
    this.getToken = getToken;
  }

  /**
   * Pede para manter a conexão viva. Idempotente — chamadas repetidas não
   * abrem sockets adicionais.
   */
  async connect(): Promise<void> {
    this.wantConnected = true;
    if (this.isConnecting) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    if (!this.url || !this.getToken) return;

    this.isConnecting = true;
    this.setStatus(this.reconnectAttempt > 0 ? 'reconnecting' : 'connecting');
    try {
      const token = await this.getToken();
      if (!token) {
        this.isConnecting = false;
        this.setStatus('closed');
        return;
      }
      if (!this.wantConnected) {
        this.isConnecting = false;
        return;
      }

      const ws = new WebSocket(`${this.url}?token=${token}`);
      this.ws = ws;

      ws.onopen = () => {
        this.reconnectAttempt = 0;
        this.lastPongAt = Date.now();
        this.setStatus('open');
        this.startPingInterval(ws);
      };

      ws.onmessage = (event) => {
        try {
          const wsEvent = JSON.parse(event.data) as WsEvent;
          if (wsEvent.type === 'pong') {
            this.lastPongAt = Date.now();
            return;
          }
          for (const handler of this.eventHandlers) {
            try {
              handler(wsEvent);
            } catch {
              // Handler error não deve afetar outros handlers nem o socket.
            }
          }
        } catch {
          // Mensagem mal-formada: ignora.
        }
      };

      ws.onclose = () => {
        if (this.ws === ws) this.ws = null;
        this.stopPingInterval();

        if (!this.wantConnected) {
          this.setStatus('closed');
          return;
        }

        this.setStatus('reconnecting');
        const delay = Math.min(
          RECONNECT_DELAY_BASE * Math.pow(2, this.reconnectAttempt),
          RECONNECT_DELAY_MAX
        );
        this.reconnectAttempt++;
        this.reconnectTimeout = setTimeout(() => {
          this.reconnectTimeout = null;
          void this.connect();
        }, delay);
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          // onclose roda o pipeline.
        }
      };
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Fecha a conexão e impede futuros reconnects.
   */
  disconnect() {
    this.wantConnected = false;
    this.reconnectAttempt = 0;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.stopPingInterval();
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // ignore
      }
      this.ws = null;
    }
    this.setStatus('closed');
  }

  subscribe(handler: EventHandler): () => void {
    this.eventHandlers.add(handler);
    return () => this.eventHandlers.delete(handler);
  }

  onStatusChange(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  getStatus(): WsStatus {
    return this.status;
  }

  private setStatus(status: WsStatus) {
    if (this.status === status) return;
    this.status = status;
    for (const handler of this.statusHandlers) {
      try {
        handler(status);
      } catch {
        // ignore handler errors
      }
    }
  }

  private startPingInterval(ws: WebSocket) {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (ws.readyState !== WebSocket.OPEN) return;
      try {
        ws.send(JSON.stringify({ action: 'ping' }));
      } catch {
        // Send falhou; onerror/onclose vão tratar.
      }
      // Pong não chega há > PONG_TIMEOUT? Trata como half-open e força reconnect.
      if (Date.now() - this.lastPongAt > PONG_TIMEOUT_MS) {
        try {
          ws.close();
        } catch {
          // onclose roda o pipeline.
        }
      }
    }, PING_INTERVAL_MS);
  }

  private stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}

export const wsClient = new WsClient();
